import { useMemo } from 'react';
import type { HexTileMapKey } from '@/game/hexmap';
import { type Command, canBlend, canFoundCity, exposeCooldownLeft, type Unit } from '@/game/sim';
import { cityName } from '@/game/view/cities';
import { groupCommand, ownGroups, ungroupCommand } from '@/game/view/groups';
import { healerStatus, healRateText } from '@/game/view/heals';
import { nameOf } from '@/game/view/names';
import {
  promoteCommands,
  promoteLabel,
  promoteTitle,
  promotionOptions,
  trainCommands,
  trainLabel,
  trainOption,
  trainTitle,
} from '@/game/view/promotions';
import { blendRejectionText, foundCityRejectionText } from '@/game/view/reasons';
import { stockInUnits } from '@/game/view/resources';
import { accuracyText } from '@/game/view/shots';
import { commonStance, fighterIds, STANCE_INFO, STANCES, stanceCommand } from '@/game/view/stance';
import { tickHealings } from '@/game/view/tickCache';
import { VERBS } from '@/game/view/verbs';
import { useSimFrame } from '../app/simFrame';
import { Cost } from '../Cost';
import { InlineRename } from '../InlineRename';
import { UnitInfoPanel } from '../UnitInfoPanel';
import { cancelRename, commitRename, startRename, type UiStore, useUi } from '../ui/store';
import { VerbLabel } from '../VerbLabel';
import { WorkerIntents } from './WorkerIntents';

/** "3,-2" → {q: 3, r: -2}. */
function parseKey(key: HexTileMapKey): { q: number; r: number } {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
}

/**
 * The bottom dock for a unit selection: a group summary (named when it's
 * exactly one group) with Automate, Group (G) / Ungroup (U) / Deploy,
 * promotions and Train for every eligible selected unit in one click (costs
 * on the buttons), and for a lone unit its chip (rank, hp, shelter), spy
 * status and actions, heal rate and status, accuracy, Automate and Found
 * City. Why an action is disabled shows on a line above the buttons, so a
 * reason coming or going never moves them.
 */
export function SelectionDock({
  dispatch,
  selectedUnitIds,
  ui,
  hidden,
  onFoundAt,
  onAttackMove,
}: {
  dispatch: (c: Command) => void;
  selectedUnitIds: readonly string[];
  /** The input store: a walking settler's Found City hint is for the tile under the cursor. */
  ui: UiStore;
  hidden: boolean;
  /** Enter the settler's "Found city at…" targeting mode. */
  onFoundAt?: (unitId: string) => void;
  /** Enter the attack-move targeting mode. */
  onAttackMove?: () => void;
}) {
  // The viewer's state (viewerState.ts); the real one only for unit caps (a
  // rival spy disguised as one of their units takes no slot). The building
  // the player knows of on a tile (fog-safe) for the Found City check.
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain, cityNumbers, knownBuilding } = frame;
  const names = useUi(ui, (s) => s.names);
  const renaming = useUi(ui, (s) => s.renaming);
  const renamingHere = renaming?.site === 'dock' ? renaming.id : null;
  const stock = stockInUnits(state, playerId);
  const selectedUnits = selectedUnitIds.flatMap((id) => state.units[id] ?? []);
  // Single-unit actions (build, found city) only apply to a lone selection.
  const selectedUnit = selectedUnits.length === 1 ? selectedUnits[0] : null;
  // Fog-safe: only cities the player knows of count as too close.
  const canFound =
    selectedUnit?.owner === playerId &&
    rules.units[selectedUnit.type]?.actions.includes('found-city');
  const foundCheck =
    selectedUnit && canFound
      ? canFoundCity(state, { terrain, rules }, playerId, selectedUnit.id, knownBuilding)
      : null;
  // A walking settler's hint is for the tile under the cursor (where it might
  // be sent), not the tiles it passes on the way; one already sent to found
  // a city (`foundAt`) gets none: its spot is picked, and ringed on the map.
  const settlerWalking = !!selectedUnit?.path?.length;
  const settlerSent = selectedUnit?.order?.kind === 'found';
  // Read only while the hint needs it, so plain hovering doesn't re-render the dock.
  const hoverHint = settlerWalking && canFound && !settlerSent;
  const hoveredKey = useUi(ui, (s) => (hoverHint ? s.hoveredKey : null));
  const hoveredCell = hoveredKey ? parseKey(hoveredKey) : null;
  const hintCheck = settlerSent
    ? null
    : !settlerWalking
      ? foundCheck
      : selectedUnit && canFound && hoveredCell
        ? canFoundCity(
            state,
            { terrain, rules },
            playerId,
            selectedUnit.id,
            knownBuilding,
            hoveredCell
          )
        : null;
  // Fighters among the selection take a stance and attack-move.
  const fighters = fighterIds(state, rules, playerId, selectedUnitIds);
  const stance = commonStance(state, fighters);
  const groupCounts = new Map<string, number>();
  for (const u of selectedUnits) groupCounts.set(u.type, (groupCounts.get(u.type) ?? 0) + 1);
  const grouping = groupCommand(state, playerId, selectedUnitIds);
  const ungrouping = ungroupCommand(state, playerId, selectedUnitIds);
  const stored = selectedUnits.filter((u) => u.owner === playerId && u.inside);
  // The selection is exactly one group: name it.
  const selectedGroup = ownGroups(state, playerId).find(
    (g) =>
      g.units.length === selectedUnits.length &&
      g.units.every((u) => selectedUnitIds.includes(u.id))
  );
  // Own units in the group that could go back on automation.
  const groupManual = selectedUnits.filter(
    (u) => u.owner === playerId && !u.auto && rules.units[u.type]?.automate
  );
  // Promotions (archer → rifle squad, worker → labourer): one button per type
  // the selection can become, counting the units one click promotes. Memoised
  // per frame and selection, so hover-only renders reuse it.
  const promotions = useMemo(
    () =>
      promotionOptions(
        frame.view,
        frame.ctx,
        frame.playerId,
        selectedUnitIds.flatMap((id) => frame.view.units[id] ?? []),
        frame.real
      ),
    [frame, selectedUnitIds]
  );
  // Training: every own military unit in the selection below the top rank,
  // one `train` each, as many as the stock covers.
  const training = useMemo(
    () =>
      trainOption(
        frame.view,
        frame.rules,
        frame.playerId,
        selectedUnitIds.flatMap((id) => frame.view.units[id] ?? [])
      ),
    [frame, selectedUnitIds]
  );
  const selectedHealText = selectedUnit ? healRateText(rules, selectedUnit) : null;
  // What a lone own healer is up to (tending on its own, sim tend.ts).
  const selectedHealer =
    selectedUnit?.owner === playerId
      ? healerStatus(state, { terrain, rules }, selectedUnit, tickHealings(state, rules))
      : null;
  const selectedAccuracy = selectedUnit ? accuracyText(rules, selectedUnit) : null;
  // Spies. An own spy: whether it can blend in where it stands, and where
  // it's disguised. Every own unit of a type spies pass for (workers) gets
  // Expose spy, whether or not it's really a spy (viewerState.ts shows a
  // disguised one as the player's own), so the button gives nothing away.
  const selectedDef = selectedUnit ? rules.units[selectedUnit.type] : undefined;
  const ownSpy = selectedUnit?.owner === playerId && !!selectedDef?.disguiseAs;
  const blendCheck =
    selectedUnit && ownSpy && !selectedUnit.disguise
      ? canBlend(state, rules, playerId, selectedUnit.id, undefined, terrain)
      : null;
  const disguiseCity = ownSpy ? state.buildings[selectedUnit?.disguise?.cityId ?? ''] : undefined;
  const exposable =
    selectedUnit?.owner === playerId &&
    Object.values(rules.units).some((u) => u.disguiseAs === selectedUnit.type);
  // Ticks are 100 ms.
  const exposeWait = Math.ceil(exposeCooldownLeft(state, playerId) / 10);
  // Why the lone unit's actions are disabled. Shown on a line above the
  // buttons, so a reason coming or going never moves them (the bar grows up).
  const hints: string[] = [];
  // A lone unit's panel says these on its Next rows.
  if (!selectedUnit && training && training.ready.length === 0 && training.reason) {
    hints.push(`${VERBS.rankUp.label}: ${training.reason}`);
  }
  for (const option of promotions) {
    if (!selectedUnit && option.ready.length === 0 && option.reason) {
      hints.push(`${rules.units[option.to]?.name ?? option.to}: ${option.reason}`);
    }
  }
  if (blendCheck && !blendCheck.ok) hints.push(blendRejectionText(blendCheck.reason));
  // Undisguised in rival land: caught once it overstays (sim spies `lurk`).
  if (ownSpy && selectedUnit.lurk !== undefined) {
    const left = Math.max(0, Math.ceil(rules.pacing.spyGrace - selectedUnit.lurk / 10));
    hints.push(`In rival land: blend in or be caught in ${left} s`);
  }
  if (hintCheck && !hintCheck.ok && selectedUnit?.inside && !settlerWalking) {
    // Waiting inside a city (where a trained settler starts): its tile is the city's.
    hints.push('In the city: move the settler out to found a city');
  } else if (hintCheck && !hintCheck.ok) {
    const text = foundCityRejectionText(rules, hintCheck, (id) => {
      const city = state.buildings[id];
      return city ? cityName(city, playerId, cityNumbers, frame.playerName, names) : null;
    });
    hints.push(settlerWalking ? `Under the cursor: ${text}` : text);
  }

  // Stance (H toggles hold) and attack-move (A), for the selection's fighters.
  const stanceControls = fighters.length > 0 && (
    <div className="flex w-full flex-wrap items-center gap-1.5">
      <span className="pr-1 text-fg-subtle">Stance</span>
      {STANCES.map((s) => {
        const command = stanceCommand(state, rules, playerId, selectedUnitIds, s);
        return (
          <button
            key={s}
            type="button"
            title={STANCE_INFO[s].help}
            onClick={() => command && dispatch(command)}
            className={`chip ${stance === s ? 'chip-selected' : ''}`}
          >
            {STANCE_INFO[s].label}
            {s === 'hold' && <kbd className="ml-1 text-fg-subtle">H</kbd>}
          </button>
        );
      })}
      <span className="flex-1" />
      {onAttackMove && (
        <button
          type="button"
          title="Walk to a tile you pick; aggressive fighters fight what they meet, held or passive ones just walk. A attack-moves (arrows pan)"
          onClick={onAttackMove}
          className="btn btn-secondary text-xs"
        >
          ⚔ Attack-move <kbd className="ml-1 text-fg-subtle">A</kbd>
          <span className="text-[10px] text-fg-subtle">(arrows pan)</span>
        </button>
      )}
    </div>
  );
  // Workers: what each is doing, Pinned / Auto, drag to re-home.
  const workerIntents = (
    <WorkerIntents
      dispatch={dispatch}
      ui={ui}
      selectedUnits={selectedUnits}
      omitJob={selectedUnit != null}
    />
  );
  const groupButtons = (grouping || ungrouping || stored.length > 0) && (
    <div className="flex items-center gap-2">
      {grouping && (
        <button
          type="button"
          title="Make the selection a group (G): they move together, following the last one ordered"
          onClick={() => dispatch(grouping)}
          className="btn btn-secondary text-xs"
        >
          Group <span className="text-fg-subtle">G</span>
        </button>
      )}
      {ungrouping && (
        <button
          type="button"
          title="Take the selected units out of their groups (U)"
          onClick={() => dispatch(ungrouping)}
          className="btn btn-secondary text-xs"
        >
          Ungroup <span className="text-fg-subtle">U</span>
        </button>
      )}
      {stored.length > 0 && (
        <button
          type="button"
          title="Bring them out of the building onto free tiles around it (a move order does too)"
          onClick={() => dispatch({ type: 'deploy', unitIds: stored.map((u) => u.id) })}
          className="btn btn-secondary text-xs"
        >
          Deploy {stored.length}
        </button>
      )}
    </div>
  );
  // The lone unit's controls (its chip, rank and hp are on the info panel).
  const unitControls = selectedUnit != null && (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {/* A spy: hidden, disguised (where) or exposed */}
      {ownSpy && (
        <span
          title={
            selectedUnit.exposed
              ? 'Exposed: everyone sees it, and it can be attacked'
              : selectedUnit.disguise
                ? "Blended in: you see this city's whole territory while it stays put"
                : `Hidden: rivals can’t see or attack it, but it's caught after ${rules.pacing.spyGrace} s in a rival city's territory unless it blends in`
          }
          className={`chip ${selectedUnit.exposed ? 'text-danger' : 'text-ok'}`}
        >
          {selectedUnit.exposed
            ? '❗ Exposed'
            : disguiseCity
              ? `🎭 Disguised in ${cityName(disguiseCity, playerId, cityNumbers, frame.playerName, names)}`
              : '👁 Hidden'}
        </span>
      )}
      {blendCheck && (
        <button
          type="button"
          disabled={!blendCheck.ok}
          title="Pass for one of their workers: you see the city's whole territory until it moves. They can expose it."
          onClick={() => dispatch({ type: 'blend', unitId: selectedUnit.id })}
          className="btn btn-secondary text-xs"
        >
          Blend in
        </button>
      )}
      {exposable && (
        <button
          type="button"
          disabled={exposeWait > 0}
          title={`If it's a rival spy disguised as your ${(selectedDef?.name ?? selectedUnit.type).toLowerCase()}, unmask it: everyone sees it and it can be attacked. If not, you can't expose again for ${rules.pacing.exposeCooldown} s.`}
          onClick={() => dispatch({ type: 'expose', unitId: selectedUnit.id })}
          className="btn btn-secondary gap-1.5 text-xs"
        >
          Expose spy
          {exposeWait > 0 && <span className="tabular-nums text-fg-muted">{exposeWait} s</span>}
        </button>
      )}

      {/* A healer: what it mends, how fast (rank included) and how far */}
      {selectedHealText && (
        <span
          title="Each tick it mends the unit in range missing the most of its hp"
          className="chip text-ok tabular-nums"
        >
          {selectedHealText}
        </span>
      )}
      {selectedHealer && (
        <span
          title="Healers tend the wounded on their own: the own unit nearby missing the most of its hp"
          className="chip"
        >
          {selectedHealer.text}
        </span>
      )}

      {/* A ranged unit: its chance to hit at full range (still targets) */}
      {selectedAccuracy && (
        <span title={selectedAccuracy.title} className="chip tabular-nums">
          {selectedAccuracy.label}
        </span>
      )}

      {/* Automation: back on for a unit that was given manual orders */}
      {rules.units[selectedUnit.type]?.automate &&
        (selectedUnit.auto ? (
          <span className="chip text-ok">
            {rules.units[selectedUnit.type]?.automate === 'explore'
              ? 'Auto-exploring'
              : 'Auto-working'}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => dispatch({ type: 'automate', unitIds: [selectedUnit.id] })}
            className="btn btn-secondary text-xs"
          >
            {rules.units[selectedUnit.type]?.automate === 'explore' ? 'Auto explore' : 'Auto work'}
          </button>
        ))}

      {/* Found City: disabled when the settler can't found here (reason above) */}
      {foundCheck && (
        <button
          type="button"
          disabled={!foundCheck.ok}
          onClick={() => dispatch({ type: 'foundCity', unitId: selectedUnit.id })}
          className="btn btn-secondary text-xs"
        >
          Found City
        </button>
      )}
      {/* Found city at…: pick a tile; the settler walks there and founds on arrival */}
      {foundCheck && onFoundAt && (
        <button
          type="button"
          title="Pick a tile (tinted): the settler walks there and founds a city when it arrives"
          onClick={() => onFoundAt(selectedUnit.id)}
          className="btn btn-secondary text-xs"
        >
          Found city at…
        </button>
      )}
    </div>
  );

  // The panel header's name: double-click (or F2) renames an own unit inline.
  const unitNameNode = (unit: Unit) => {
    const typeName = rules.units[unit.type]?.name ?? unit.type;
    const name = nameOf(names, unit.id);
    if (renamingHere === unit.id) {
      return (
        <InlineRename
          value={name ?? ''}
          label={`Rename ${typeName}`}
          onCommit={(next) => commitRename(ui, unit.id, next)}
          onCancel={() => cancelRename(ui)}
          className="w-32"
        />
      );
    }
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: double-click is a shortcut; F2 renames from the keyboard
      <span
        title={unit.owner === playerId ? 'Double-click or F2 to rename' : undefined}
        onDoubleClick={() => unit.owner === playerId && startRename(ui, unit.id, 'dock')}
      >
        {name ? (
          <>
            <span className="font-medium">{name}</span>
            <span className="text-fg-muted"> · {typeName}</span>
          </>
        ) : (
          typeName
        )}
      </span>
    );
  };

  if (hidden || selectedUnits.length === 0) return null;
  if (selectedUnit) {
    return (
      <UnitInfoPanel
        unit={selectedUnit}
        training={training}
        promotions={promotions}
        dispatch={dispatch}
        onClose={() => ui.set({ selectedUnitIds: [] })}
        names={names}
        nameNode={unitNameNode(selectedUnit)}
        footer={
          <>
            {hints.length > 0 && <p className="px-1 text-[11px] text-warn">{hints.join(' · ')}</p>}
            {stanceControls}
            {workerIntents}
            {(groupButtons || unitControls) && (
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {groupButtons}
                {unitControls}
              </div>
            )}
          </>
        }
      />
    );
  }
  return (
    <div className="panel hud-dock flex flex-wrap items-center gap-2 p-2 text-xs">
      {hints.length > 0 && <p className="w-full px-1 text-[11px] text-warn">{hints.join(' · ')}</p>}
      {/* Group selection — right-click moves them all */}
      {selectedUnits.length > 1 && (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* biome-ignore lint/a11y/noStaticElementInteractions: double-click is a shortcut; F2 renames from the keyboard */}
          <span
            className="chip min-w-0 flex-1"
            title={selectedGroup ? 'Double-click or F2 to rename the group' : undefined}
            onDoubleClick={() => selectedGroup && startRename(ui, selectedGroup.id, 'dock')}
          >
            <span className="truncate">
              {selectedGroup &&
                (renamingHere === selectedGroup.id ? (
                  <InlineRename
                    value={nameOf(names, selectedGroup.id) ?? ''}
                    label={`Rename Group ${selectedGroup.number}`}
                    onCommit={(name) => commitRename(ui, selectedGroup.id, name)}
                    onCancel={() => cancelRename(ui)}
                    className="mr-1 w-28"
                  />
                ) : (
                  <>
                    <span
                      className={nameOf(names, selectedGroup.id) ? 'font-medium' : 'text-fg-muted'}
                    >
                      {nameOf(names, selectedGroup.id) ?? `Group ${selectedGroup.number}`}
                    </span>
                    {' · '}
                  </>
                ))}
              {selectedUnits.length} units:{' '}
              {[...groupCounts]
                .map(([type, n]) => `${n} ${rules.units[type]?.name ?? type}`)
                .join(', ')}
            </span>
          </span>
          {groupManual.length > 0 && (
            <button
              type="button"
              onClick={() => dispatch({ type: 'automate', unitIds: groupManual.map((u) => u.id) })}
              className="btn btn-secondary text-xs"
            >
              Automate {groupManual.length}
            </button>
          )}
        </div>
      )}

      {stanceControls}
      {workerIntents}
      {groupButtons}

      {/* Promote: turn the selected units into a later type in place, every
          ready one in one click (the cost of all on the button). The same upgrade / rank-up glyphs
          as the Units panel's type actions; a minimum width and tabular numbers,
          so a count changing as the stock does never resizes the buttons. */}
      {(promotions.length > 0 || training) && (
        <div className="flex w-full flex-wrap items-center gap-2">
          {/* Rank up: buy the next rank at a barracks in their city's territory */}
          {training && (
            <button
              type="button"
              disabled={training.ready.length === 0}
              title={trainTitle(rules, training)}
              onClick={() => {
                for (const command of trainCommands(training)) dispatch(command);
              }}
              className="btn btn-secondary min-w-44 justify-between gap-1.5 text-xs tabular-nums"
            >
              <span>
                <VerbLabel verb="rankUp">{trainLabel(rules, training)}</VerbLabel>
              </span>
              <span className="tabular-nums text-fg-muted">
                <Cost cost={training.cost} stock={stock} />
              </span>
            </button>
          )}
          {promotions.map((option) => (
            <button
              key={option.to}
              type="button"
              disabled={option.ready.length === 0}
              title={promoteTitle(rules, option)}
              onClick={() => {
                for (const command of promoteCommands(option)) dispatch(command);
              }}
              className="btn btn-secondary min-w-44 justify-between gap-1.5 text-xs tabular-nums"
            >
              <span>
                <VerbLabel verb="upgrade">{promoteLabel(rules, option)}</VerbLabel>
              </span>
              <span className="tabular-nums text-fg-muted">
                <Cost cost={option.cost} stock={stock} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
